import { useState } from 'react';
import { Button } from '../components/primitives';
import { SideModal, LabeledText, LabeledArea, LabeledSelect } from '../components/SideModal';
import { US_STATES } from '../app/forms';
import { api } from '../lib/api';
import type { ClientLocation, Contact, Contract } from '../../shared/types';

const STATE_OPTS = [{ label: '—', value: '' }, ...US_STATES.map((s) => ({ label: s, value: s }))];

// Location + contact editors for a client. Extracted from DrawerHost so the
// Client detail *page* can launch them as standalone drawers (no modal stacking).
export function LocationEditor({ clientId, location, onClose, onSaved }: { clientId: number; location: ClientLocation | null; onClose: () => void; onSaved: () => void }) {
  const [label, setLabel] = useState(location?.label || '');
  const [isHq, setIsHq] = useState(!!location?.is_hq);
  const [line1, setLine1] = useState(location?.addr_line1 || '');
  const [line2, setLine2] = useState(location?.addr_line2 || '');
  const [city, setCity] = useState(location?.city || '');
  const [state, setState] = useState(location?.state || '');
  const [zip, setZip] = useState(location?.zip || '');
  const [facilityType, setFacilityType] = useState(location?.facility_type || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { label, is_hq: isHq, addr_line1: line1, addr_line2: line2, city, state, zip, facility_type: facilityType };
      if (location) await api.put(`/locations/${location.id}`, payload);
      else await api.post(`/clients/${clientId}/locations`, payload);
      onSaved();
    } finally {
      setBusy(false);
    }
  };
  const del = async () => {
    if (!location || !confirm(`Delete location "${location.label}"?`)) return;
    await api.del(`/locations/${location.id}`);
    onSaved();
  };

  return (
    <SideModal
      title={location ? 'Edit location' : 'Add location'}
      sub="Full address — the ZIP is geocoded for assignment proximity"
      onClose={onClose}
      footer={
        <>
          {location ? <Button variant="danger" onClick={del}>Delete</Button> : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={busy || !label}>{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </>
      }
    >
      <LabeledText label="Label" value={label} onChange={setLabel} placeholder="e.g. Headquarters, El Paso Facility" />
      <LabeledSelect label="Is this the HQ?" value={isHq ? 'yes' : 'no'} onChange={(v) => setIsHq(v === 'yes')} options={[{ label: 'No', value: 'no' }, { label: 'Yes — headquarters', value: 'yes' }]} />
      <LabeledText label="Street address" value={line1} onChange={setLine1} placeholder="Street address" />
      <LabeledText label="Address line 2" value={line2} onChange={setLine2} placeholder="Suite, unit (optional)" />
      <LabeledText label="City" value={city} onChange={setCity} placeholder="City" />
      <LabeledSelect label="State" value={state} onChange={setState} options={STATE_OPTS} />
      <LabeledText label="ZIP code" value={zip} onChange={setZip} placeholder="5-digit ZIP" mono />
      <LabeledSelect label="Facility type" value={facilityType} onChange={setFacilityType} options={[{ label: '—', value: '' }, { label: 'Adult Prison', value: 'Adult Prison' }, { label: 'Community Confinement Center', value: 'Community Confinement Center' }, { label: 'Juvenile', value: 'Juvenile' }]} />
    </SideModal>
  );
}

export function ContactEditor({ clientId, contact, locations, onClose, onSaved }: { clientId: number; contact: Contact | null; locations: ClientLocation[]; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(contact?.name || '');
  const [title, setTitle] = useState(contact?.title || '');
  const [email, setEmail] = useState(contact?.email || '');
  const [phone, setPhone] = useState(contact?.phone || '');
  const [primary, setPrimary] = useState(!!contact?.primary);
  const [locationId, setLocationId] = useState(contact?.location_id ? String(contact.location_id) : '');
  const [notes, setNotes] = useState(contact?.notes || '');
  const [busy, setBusy] = useState(false);

  const locOpts = [{ label: 'Client-level (no specific location)', value: '' }, ...locations.map((l) => ({ label: `${l.label}${l.is_hq ? ' (HQ)' : ''}`, value: String(l.id) }))];

  const save = async () => {
    setBusy(true);
    try {
      const payload = { client_id: clientId, location_id: locationId ? Number(locationId) : null, name, title, email, phone, primary, notes };
      if (contact) await api.put(`/contacts/${contact.id}`, payload);
      else await api.post('/contacts', payload);
      onSaved();
    } finally {
      setBusy(false);
    }
  };
  const del = async () => {
    if (!contact || !confirm(`Delete contact "${contact.name}"?`)) return;
    await api.del(`/contacts/${contact.id}`);
    onSaved();
  };

  return (
    <SideModal
      title={contact ? 'Edit contact' : 'Add contact'}
      sub="Assign to the client overall or a specific location"
      onClose={onClose}
      footer={
        <>
          {contact ? <Button variant="danger" onClick={del}>Delete</Button> : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={busy || !name}>{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </>
      }
    >
      <LabeledText label="Full name" value={name} onChange={setName} placeholder="e.g. Hannah Booth" />
      <LabeledText label="Job title" value={title} onChange={setTitle} placeholder="e.g. Operations Lead" />
      <LabeledSelect label="Assigned to" value={locationId} onChange={setLocationId} options={locOpts} />
      <LabeledText label="Email" value={email} onChange={setEmail} placeholder="name@company.com" />
      <LabeledText label="Phone" value={phone} onChange={setPhone} placeholder="+1 …" />
      <LabeledSelect label="Primary contact" value={primary ? 'yes' : 'no'} onChange={(v) => setPrimary(v === 'yes')} options={[{ label: 'No', value: 'no' }, { label: 'Yes', value: 'yes' }]} />
      <LabeledArea label="Notes" value={notes} onChange={setNotes} rows={3} placeholder="Role, best time to reach, etc." />
    </SideModal>
  );
}

export function ContractEditor({ clientId, contract, onClose, onSaved }: { clientId: number; contract: Contract | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(contract?.name || '');
  const [reference, setReference] = useState(contract?.reference || '');
  const [agency, setAgency] = useState(contract?.agency || '');
  const [start, setStart] = useState(contract?.start_date || '');
  const [end, setEnd] = useState(contract?.end_date || '');
  const [notes, setNotes] = useState(contract?.notes || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { client_id: clientId, name, reference, agency, start_date: start, end_date: end, notes };
      if (contract) await api.put(`/contracts/${contract.id}`, payload);
      else await api.post('/contracts', payload);
      onSaved();
    } finally {
      setBusy(false);
    }
  };
  const del = async () => {
    if (!contract || !confirm(`Delete contract "${contract.name}"? Projects linked to it will be unlinked.`)) return;
    await api.del(`/contracts/${contract.id}`);
    onSaved();
  };

  return (
    <SideModal
      title={contract ? 'Edit contract' : 'Add contract / PO'}
      sub="Group projects (e.g. facility audits) under one agency contract or PO"
      onClose={onClose}
      footer={
        <>
          {contract ? <Button variant="danger" onClick={del}>Delete</Button> : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={busy || !name}>{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </>
      }
    >
      <LabeledText label="Contract name" value={name} onChange={setName} placeholder="e.g. TDCJ 2024–25 PREA Audits" />
      <LabeledText label="PO / contract #" value={reference} onChange={setReference} placeholder="e.g. 696-AR-24-25-C135" mono />
      <LabeledText label="Agency" value={agency} onChange={setAgency} placeholder="e.g. Texas DCJ" />
      <LabeledText label="Start date" value={start} onChange={setStart} placeholder="e.g. 2025-01-01" />
      <LabeledText label="End date" value={end} onChange={setEnd} placeholder="e.g. 2025-12-31" />
      <LabeledArea label="Notes" value={notes} onChange={setNotes} rows={3} placeholder="Billing instructions, scope, etc." />
    </SideModal>
  );
}
